import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Business from "@mui/icons-material/Business";
import CardGiftcardOutlined from "@mui/icons-material/CardGiftcardOutlined";
import CellTower from "@mui/icons-material/CellTower";
import DataObject from "@mui/icons-material/DataObject";
import Folder from "@mui/icons-material/Folder";
import Groups from "@mui/icons-material/Groups";
import HelpOutline from "@mui/icons-material/HelpOutline";
import Logout from "@mui/icons-material/Logout";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Settings from "@mui/icons-material/Settings";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  SvgIcon,
} from "@mui/material";
import { useState } from "react";
import { Link } from "react-router";
import { usePermissions } from "../../../../server/auth/use-permissions";
import { useFlag } from "../../../../server/optimizely";
import { useSupabase } from "../../../../server/supabase/hooks";
import { usePopover } from "../../../../utils/hooks/use-popover";
import { SupabaseBrandKitService } from "../../../brand-kit/server/brand-kit-service";
import { ThemeToggle } from "../../../generic/components/settings/theme-toggle";
import { AccountSwitcher } from "./account-switcher";
import { ThemeAwareDivider } from "./divider";

import type { Login } from "../../../pipe/types";
import type { FC } from "react";

interface SettingsMenuProps {
  size?: "small" | "medium" | "large";
  logins?: { [k: string]: Login };
}

export const SettingsMenu: FC<SettingsMenuProps> = ({ size = "medium", logins = {} }) => {
  const [anchorRef, popover] = usePopover<HTMLButtonElement>();

  return (
    <>
      <IconButton
        data-analytics-id="settings-menu"
        ref={anchorRef}
        onClick={popover.handleOpen}
        size={size}
        data-testid="settings-button"
      >
        <SvgIcon data-testid="settings-menu-chevron" fontSize={size}>
          <Settings />
        </SvgIcon>
      </IconButton>
      <SettingsPopover
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        logins={logins}
      />
    </>
  );
};

const SettingsPopover: FC<
  {
    anchorEl: null | Element;
    onClose: () => void;
    open: boolean;
  } & Pick<SettingsMenuProps, "logins">
> = ({ anchorEl, onClose, open, logins }) => {
  const { super_admin: is_super_admin } = usePermissions();
  const [tremendousEnabled] = useFlag("tremendous_integration");
  const [aiPiiClickwrapEnabled] = useFlag("ai_pii_clickwrap");

  const [logo, setLogo] = useState<{ src: string } | undefined>();

  useSupabase(async ({ supabase, account_id }) => {
    const { data: logoData } = await new SupabaseBrandKitService(supabase).getDefaultLogo(
      account_id,
    );

    setLogo(logoData?.brand_logo ?? undefined);
  }, []);

  return (
    <Menu
      anchorEl={anchorEl}
      disableScrollLock
      anchorOrigin={{
        horizontal: "left",
        vertical: "bottom",
      }}
      transformOrigin={{
        horizontal: "left",
        vertical: "top",
      }}
      keepMounted
      onClose={onClose}
      open={open}
      slotProps={{ paper: { sx: { width: "250px" } } }}
    >
      <MenuList>
        {is_super_admin && (
          <MenuItem component={Link} to="/manage/brand" onClick={onClose}>
            <ListItemIcon>
              {logo ? (
                <Box
                  sx={{
                    height: 24,
                    width: 24,
                  }}
                >
                  <img
                    alt="logo"
                    src={logo.src}
                    style={{
                      objectFit: "contain",
                      height: "100%",
                      width: "100%",
                    }}
                  />
                </Box>
              ) : (
                <Business />
              )}
            </ListItemIcon>
            <ListItemText>Your Brand</ListItemText>
          </MenuItem>
        )}
        {is_super_admin && open && (
          <MenuItem component={Link} to="/manage/employees" onClick={onClose}>
            <ListItemIcon>
              <Groups />
            </ListItemIcon>
            <ListItemText>Employees</ListItemText>
          </MenuItem>
        )}
        {is_super_admin && open && (
          <MenuItem component={Link} to="/manage/channels" onClick={onClose}>
            <ListItemIcon>
              <CellTower />
            </ListItemIcon>
            <ListItemText>Platforms</ListItemText>
          </MenuItem>
        )}
        {is_super_admin && open && (
          <MenuItem component={Link} to="/manage/programs" onClick={onClose}>
            <ListItemIcon>
              <Folder />
            </ListItemIcon>
            <ListItemText>Programs</ListItemText>
          </MenuItem>
        )}
        {is_super_admin && open && (
          <MenuItem component={Link} to="/manage/segments/standard" onClick={onClose}>
            <ListItemIcon>
              <PersonAdd />
            </ListItemIcon>
            <ListItemText>Segments</ListItemText>
          </MenuItem>
        )}
        {is_super_admin && open && (
          <MenuItem component={Link} to="/manage/employee_data" onClick={onClose}>
            <ListItemIcon>
              <DataObject />
            </ListItemIcon>
            <ListItemText>Events &amp; Merge Tags</ListItemText>
          </MenuItem>
        )}
        {is_super_admin && open && tremendousEnabled && (
          <MenuItem component={Link} to="/gifting" onClick={onClose}>
            <ListItemIcon>
              <CardGiftcardOutlined />
            </ListItemIcon>
            <ListItemText>eGifting</ListItemText>
          </MenuItem>
        )}
        {is_super_admin && open && aiPiiClickwrapEnabled && (
          <MenuItem component={Link} to="/manage/ai" onClick={onClose}>
            <ListItemIcon>
              <AutoAwesome />
            </ListItemIcon>
            <ListItemText>AI Assistant</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          component={Link}
          to="https://help.changeengine.com"
          target="_blank"
          rel="noreferrer"
          onClick={onClose}
        >
          <ListItemIcon>
            <HelpOutline />
          </ListItemIcon>
          <ListItemText>Help &amp; Support</ListItemText>
        </MenuItem>
        <MenuItem sx={{ cursor: "default" }}>
          <ThemeToggle />
        </MenuItem>
        <ThemeAwareDivider />

        <AccountSwitcher logins={logins} />

        <MenuItem component={Link} to="/logout" onClick={onClose}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
